import React, { useState, useEffect } from 'react';
import { Card, ProgressBar, Button, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { bronzeMedal, silverMedal, goldMedal, platinumMedal, ironMedal } from './images/jpg/medalimages';
import '../scss/loyalty-status-card.component.scss';

ChartJS.register(ArcElement, Tooltip);

/* Smoothly counts a number up from 0 to target (synced with the bar fill) */
const useCountUp = (target: number, duration = 1300): number => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        const reduce = typeof window !== 'undefined' && window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce || !target) {
            setValue(target);
            return;
        }
        let raf = 0;
        const start = performance.now();
        const tick = (now: number) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
            setValue(target * eased);
            if (p < 1) {
                raf = requestAnimationFrame(tick);
            } else {
                setValue(target);
            }
        };
        setValue(0);
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return value;
};

interface LoyaltyStatusCardProps {
    userPoints: number;
    currentLevel: string;
    maxPoints: number;
    discountPercentage: number
}

const LoyaltyStatusCard: React.FC<LoyaltyStatusCardProps> = ({
    userPoints,
    currentLevel,
    maxPoints,
    discountPercentage
}) => {
    const { t, i18n } = useTranslation();
    // Check if current language is Swedish to apply smaller font
    const isSwedish = i18n.language === 'sv-se' || i18n.language === 'sv';

    // Format numbers with dots for Swedish, commas for others
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat(isSwedish ? 'sv-SE' : 'en-US').format(num);
    };

    const progressPercentage = (userPoints / maxPoints) * 100;

    // Doughnut chart — visual progress to the next reward
    const remainingPoints = Math.max(maxPoints - userPoints, 0);
    const progressPct = maxPoints > 0 ? Math.min(Math.round((userPoints / maxPoints) * 100), 100) : 0;
    const chartData = {
        labels: ['', ''],
        datasets: [{
            data: [Math.max(userPoints, 0), remainingPoints],
            backgroundColor: ['#FCD53F', 'rgba(255, 255, 255, 0.16)'],
            hoverBackgroundColor: ['#FFE16B', 'rgba(255, 255, 255, 0.16)'],
            borderWidth: 0,
            borderRadius: 12,
            spacing: 2,
        }],
    };
    // animated (count-up) values, synced with the bar fill
    const animatedDiscount = useCountUp(discountPercentage || 0);
    const animatedPoints = useCountUp(Math.max(userPoints, 0));
    const animatedPct = useCountUp(progressPct);
    const animatedRemaining = Math.max(maxPoints - Math.round(animatedPoints), 0);

    const chartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '76%',
        rotation: -90,
        animation: {
            animateRotate: true,
            animateScale: false,
            duration: 1300,
            easing: 'easeOutQuart',
        },
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
    };

    const getMedalImage = (level: string) => {
        switch (level.toLowerCase()) {
            case 'bronze':
                return bronzeMedal;
            case 'silver':
                return silverMedal;
            case 'gold':
                return goldMedal;
            case 'platinum':
                return platinumMedal;
            default:
                return ironMedal;
        }
    };
    const getCurrentLevel = (level: string) => {
        switch (level.toLowerCase()) {
            case 'bronze':
                return t('LOYALTY_PROGRAM_BRONZE_LEVEL');
            case 'silver':
                return t('LOYALTY_PROGRAM_SILVER_LEVEL');
            case 'gold':
                return t('LOYALTY_PROGRAM_GOLD_LEVEL');
            case 'platinum':
                return t('LOYALTY_PROGRAM_PLATINUM_LEVEL');
            default:
                return t('LOYALTY_PROGRAM_IRON_LEVEL');
        }
    };
    const getProgressBarColor = (level: string) => {
        switch (level.toLowerCase()) {
            case 'bronze':
                return 'BRONZE';
            case 'silver':
                return 'SILVER';
            case 'gold':
                return 'GOLD'
            case 'platinum':
                return 'PlATINUM';
            default:
                return 'BRONZE'
        }
    };


    return (
        <Card className="loyalty-status-card">
            <div className="background"></div>
            <Card.Body className="p-0 w-75.5">
                <div className="text-center mb-4">
                    <Card.Title as="h3" className="loyalty-status-title mb-3">
                        {t('LOYALTY_PROGRAM_YOUR_LOYALTY_STATUS')}
                    </Card.Title>
                    <Card.Text className="loyalty-status-subtitle">
                        {t('LOYALTY_PROGRAM_STATUS_DESCRIPTION')}
                    </Card.Text>
                    <div className="current-reward-badge">
                        <span className="current-reward-text">{t('LOYALTY_PROGRAM_YOUR_CURRENT_REWARD')}</span>
                    </div>
                </div>

                <div className="status-badge mb-4">
                    <div className="current-reward text-center">
                        <div className="medal-icon-wrapper">
                            {getMedalImage(currentLevel)}
                        </div>
                        <Card.Title as="h4" className="level-title mb-2">
                            {getCurrentLevel(currentLevel)}
                        </Card.Title>
                        <Card.Text className="level-description">
                            {t('LOYALTY_PROGRAM_PROGRESS_TEXT')}
                        </Card.Text>
                    </div>
                </div>
                <Row className="align-items-center mb-3 mt-3 mx-0">
                    <Col className="px-0">
                        <span className="points-label">
                            {t('INVOICE_DISCOUNT')}
                        </span>
                    </Col>
                    <Col xs="auto" className="px-0">
                        <span className="points-value">
                            {Math.round(animatedDiscount)} % {t('OFF')}
                        </span>
                    </Col>
                </Row>
                <div className="points-section w-100">
                    <ProgressBar
                        now={progressPercentage}
                        className=" progress-bar-custom"
                        variant={getProgressBarColor(currentLevel)}
                    />
                    <Row className="align-items-center mb-3 mt-3 mx-0">
                        <Col className="px-0">
                            <span className="points-label">
                                {t('LOYALTY_PROGRAM_YOUR_POINTS')}
                            </span>
                        </Col>
                        <Col xs="auto" className="px-0">
                            <span className="points-value">
                                {formatNumber(Math.round(animatedPoints))} / {formatNumber(maxPoints)}
                            </span>
                        </Col>
                    </Row>
                </div>

                <div className="status-chart">
                    <div className="status-chart__ring">
                        <Doughnut data={chartData} options={chartOptions} />
                        <div className="status-chart__center">
                            <span className="status-chart__pct">{Math.round(animatedPct)}%</span>
                            <span className="status-chart__label">
                                {t('LOYALTY_PROGRAM_NEXT_REWARD_PROGRESS')}
                            </span>
                        </div>
                    </div>
                    <span className="status-chart__caption">
                        {formatNumber(animatedRemaining)} {t('LOYALTY_PROGRAM_POINTS_LEFT')}
                    </span>
                </div>
            </Card.Body>
        </Card>
    );
};

export default LoyaltyStatusCard;
